import { CuriosityAutocomplete, CuriosityNamespace, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctContractDemandFilterField, TsrctContractDemandStatus } from '@/generated/gql/graphql';
import {
  listOperators,
  operators,
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';
import {
  ValueEditorSelectAutocomplete,
  ValueEditorSelectAutocompleteProps,
} from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

enum ContractDemandQueryFieldGroup {
  DEMAND = 'demand',
  CASE = 'case',
}

const curiosityNsMap: { [key in TsrctContractDemandFilterField]?: CuriosityNamespace } = {
  [TsrctContractDemandFilterField.commodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [TsrctContractDemandFilterField.responsibleUserId]: CuriosityNamespaceType.INTERNAL_USER,
};

const enumAutocompleteMap: {
  [key in TsrctContractDemandFilterField]?: Pick<
    ValueEditorSelectAutocompleteProps,
    'initialI18nPrefix' | 'initialOptions'
  >;
} = {
  [TsrctContractDemandFilterField.priority]: {
    initialI18nPrefix: 'ContractDemandPriority',
    initialOptions: [0, 1, 2, 3],
  },
  [TsrctContractDemandFilterField.status]: {
    initialI18nPrefix: 'ContractDemandStatus',
    initialOptions: Object.keys(TsrctContractDemandStatus),
  },
};

export const useContractDemandDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'ContractDemandQueryBuilder' });
    return {
      fields: [
        // ---------- DEMAND ----------
        createField({
          name: TsrctContractDemandFilterField.sequenceNumber,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: [...operators.numNotEmpty, ...operators.idAuto],
        }),
        createField({
          name: TsrctContractDemandFilterField.status,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: TsrctContractDemandFilterField.responsibleUserId,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctContractDemandFilterField.priority,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: TsrctContractDemandFilterField.commodityGroupId,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctContractDemandFilterField.createdAt,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.date,
        }),
        createField({
          name: TsrctContractDemandFilterField.validFrom,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.date,
        }),
        createField({
          name: TsrctContractDemandFilterField.validTo,
          grp: ContractDemandQueryFieldGroup.DEMAND,
          operators: operators.date,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case TsrctContractDemandFilterField.validFrom:
          case TsrctContractDemandFilterField.validTo:
          case TsrctContractDemandFilterField.createdAt:
          case TsrctContractDemandFilterField.updatedAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          //
          case TsrctContractDemandFilterField.sequenceNumber:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }

            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple freeSolo onlyNumber onlyPositive />;
            }

            return <ValueEditorNumberInput min={0} {...otherProps} />;
          case TsrctContractDemandFilterField.status:
          case TsrctContractDemandFilterField.priority: {
            const enumOptions = enumAutocompleteMap[field];
            if (enumOptions) {
              return (
                <ValueEditorSelectAutocomplete {...otherProps} {...enumOptions} multiple={listOperators[operator]} />
              );
            }
            return null;
          }
          case TsrctContractDemandFilterField.responsibleUserId:
          case TsrctContractDemandFilterField.commodityGroupId: {
            const ns = curiosityNsMap[field];
            if (ns) {
              return <CuriosityAutocomplete ns={ns} multiple={listOperators[operator]} simplifyValue {...otherProps} />;
            }
            return null;
          }
          case TsrctContractDemandFilterField.updatedAt:
          case TsrctContractDemandFilterField.createdAt:
          case TsrctContractDemandFilterField.validFrom:
          case TsrctContractDemandFilterField.validTo:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
